import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "@/lib/api";

export interface TradingSummary {
  login: number;
  /** Which platform this account lives on. Login numbers are assigned per
   *  platform, so the same number can exist on both — always show this. */
  platform?: string;
  name: string;
  email: string;
  group: string;
  accountType: "live" | "demo";
  leverage: number;
  balance: number;
  credit: number;
  equity: number;
  margin: number;
  freeMargin: number;
  marginLevel: number;
  currency: string;
  status: "active" | "blocked" | "archived";
  registeredAt: string;
  lastAccessAt: string | null;
}

export interface TradingDetail extends TradingSummary {
  password?: string;
  investorPassword?: string;
  country: string | null;
  phone: string | null;
  comment: string | null;
}

export interface TradeDeal {
  dealId: number;
  login: number;
  symbol: string | null;
  action: string;
  entryType: string | null;
  volume: number | null;
  priceOpen: number | null;
  priceClose: number | null;
  profit: number;
  commission: number;
  swap: number;
  comment: string | null;
  timeOpen: string | null;
  timeClose: string;
}

export interface TradePosition {
  positionId: number;
  login: number;
  symbol: string;
  action: "buy" | "sell";
  volume: number;
  priceOpen: number;
  priceCurrent: number | null;
  profit: number;
  swap: number;
  comment: string | null;
  timeOpen: string;
}

export interface TradingServerInfo {
  /** Which platform this status is about. */
  platform?: string;
  build: string;
  serverName: string;
  serverTime: string;
  totalAccounts: number;
  totalLive: number;
  totalDemo: number;
  totalBalanceUsd: number;
  totalEquityUsd: number;
  totalOpenPositions: number;
  mode: "sim" | "live";
}

export function useTradingServerInfo(platform?: string) {
  return useQuery({
    queryKey: ["trading-server-info", platform ?? ""],
    queryFn: async () => unwrap<TradingServerInfo>(
      (await api.get(`/admin/trading/server-info${platform ? `?platform=${encodeURIComponent(platform)}` : ""}`)).data,
    ),
    staleTime: 30_000,
  });
}

export interface AccountsFilter {
  accountType?: "live" | "demo";
  group?: string;
  status?: string;
  q?: string;
  /** One platform, or blank for every platform the broker runs, merged. */
  platform?: string;
  offset?: number;
  limit?: number;
}

/** The platforms this broker runs, and which one is the default. Drives every
 *  platform filter and the "which platform" choice on a group or an account. */
export function useTradingPlatforms() {
  return useQuery({
    queryKey: ["trading-platforms"],
    queryFn: async () =>
      unwrap<{ platforms: string[]; default: string }>((await api.get("/admin/trading/platforms")).data),
    staleTime: 60_000,
  });
}

export function useTradingAccounts(filter: AccountsFilter = {}) {
  return useQuery({
    queryKey: ["trading-accounts", filter],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([k, v]) => { if (v != null && v !== "") params.set(k, String(v)); });
      return unwrap<{ rows: TradingSummary[]; total: number }>((await api.get(`/admin/trading/accounts?${params.toString()}`)).data);
    },
    staleTime: 15_000,
  });
}

export function useTradingAccount(login: number | null) {
  return useQuery({
    queryKey: ["trading-account", login],
    queryFn: async () => unwrap<TradingDetail>((await api.get(`/admin/trading/accounts/${login}`)).data),
    enabled: !!login,
  });
}

export interface CreateAccountInput {
  name: string;
  email: string;
  group: string;
  accountType: "live" | "demo";
  leverage: number;
  country?: string;
  phone?: string;
  comment?: string;
  initialDeposit?: number;
}

export function useCreateTradingAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateAccountInput) => unwrap<TradingDetail>((await api.post("/admin/trading/accounts", input)).data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["trading-accounts"] });
      qc.invalidateQueries({ queryKey: ["trading-server-info"] });
    },
  });
}

export function useUpdateTradingAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { login: number; patch: Partial<{ leverage: number; group: string; status: string; comment: string }> }) =>
      unwrap<TradingDetail>((await api.patch(`/admin/trading/accounts/${v.login}`, v.patch)).data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["trading-accounts"] }),
  });
}

export function useBlockTradingAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (login: number) => unwrap<TradingDetail>((await api.post(`/admin/trading/accounts/${login}/block`)).data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["trading-accounts"] }),
  });
}

export function useUnblockTradingAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (login: number) => unwrap<TradingDetail>((await api.post(`/admin/trading/accounts/${login}/unblock`)).data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["trading-accounts"] }),
  });
}

export interface BalanceOpInput { login: number; amount: number; comment?: string; }

export function useTradingDeposit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: BalanceOpInput) =>
      unwrap<{ dealId: number; newBalance: number }>((await api.post("/admin/trading/deposit", input)).data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["trading-accounts"] });
      qc.invalidateQueries({ queryKey: ["trading-history"] });
      qc.invalidateQueries({ queryKey: ["trading-server-info"] });
    },
  });
}

export function useTradingWithdraw() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: BalanceOpInput) =>
      unwrap<{ dealId: number; newBalance: number }>((await api.post("/admin/trading/withdraw", input)).data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["trading-accounts"] });
      qc.invalidateQueries({ queryKey: ["trading-history"] });
      qc.invalidateQueries({ queryKey: ["trading-server-info"] });
    },
  });
}

export function useTradingCredit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: BalanceOpInput) =>
      unwrap<{ dealId: number; newBalance: number }>((await api.post("/admin/trading/credit", input)).data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["trading-accounts"] });
      qc.invalidateQueries({ queryKey: ["trading-history"] });
    },
  });
}

export interface HistoryFilter {
  login?: number;
  symbol?: string;
  from?: string;
  to?: string;
  offset?: number;
  limit?: number;
}

export function useTradingHistory(filter: HistoryFilter = {}) {
  return useQuery({
    queryKey: ["trading-history", filter],
    queryFn: async () => {
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([k, v]) => { if (v != null && v !== "") params.set(k, String(v)); });
      return unwrap<{ rows: TradeDeal[]; total: number }>((await api.get(`/admin/trading/trade-history?${params.toString()}`)).data);
    },
    staleTime: 15_000,
  });
}

export function useTradingPositions(login?: number) {
  return useQuery({
    queryKey: ["trading-positions", login],
    queryFn: async () => unwrap<{ rows: TradePosition[] }>((await api.get(`/admin/trading/positions${login ? `?login=${login}` : ""}`)).data),
    staleTime: 10_000,
  });
}

export function useClosePosition() {
  const qc = useQueryClient();
  return useMutation({
    // `login` lets the platform find the ticket in that account's own list —
    // not every platform can resolve a ticket from a platform-wide list.
    mutationFn: async (v: { positionId: number; comment?: string; login?: number }) =>
      unwrap<{ dealId: number; profit: number }>(
        (await api.post(`/admin/trading/positions/${v.positionId}/close`, { comment: v.comment, login: v.login })).data,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["trading-positions"] });
      qc.invalidateQueries({ queryKey: ["trading-accounts"] });
      qc.invalidateQueries({ queryKey: ["trading-history"] });
    },
  });
}

/** The server groups of ONE platform. Group names (e.g. "real\\Standard") differ
 *  between platforms, so a group must be mapped against the right list. */
export function usePlatformGroups(platform?: string) {
  return useQuery({
    queryKey: ["platform-groups", platform ?? ""],
    queryFn: async () => unwrap<{ rows: string[] }>(
      (await api.get(`/admin/trading/groups${platform ? `?platform=${encodeURIComponent(platform)}` : ""}`)).data,
    ),
    staleTime: 5 * 60_000,
  });
}

/** Spread / commission / leverage exactly as configured on the trading platform. */
export interface PlatformGroupConditions {
  group: string;
  commissionPerLot: { min: number; max: number } | null;
  spreadPips: { min: number; max: number } | null;
  spreadSymbols: number;
  spreadType: string | null;
  minLeverage: number | null;
  maxLeverage: number | null;
  stopOutPct: number | null;
}

export function usePlatformGroupConditions() {
  return useQuery({
    queryKey: ["platform-group-conditions"],
    queryFn: async () =>
      unwrap<{ supported: boolean; rows: PlatformGroupConditions[]; error?: string }>(
        (await api.get("/admin/trading-groups/platform-conditions")).data,
      ),
    staleTime: 60_000,
  });
}

export function useTradingSymbols() {
  return useQuery({
    queryKey: ["trading-symbols"],
    queryFn: async () => unwrap<{ rows: string[] }>((await api.get("/admin/trading/symbols")).data),
    staleTime: 5 * 60_000,
  });
}

export function useSeedTrading() {
  const qc = useQueryClient();
  return useMutation<{ accountsCreated: number; dealsCreated: number; positionsCreated: number; skipped?: boolean }, Error, boolean | undefined>({
    mutationFn: async (force) =>
      unwrap<{ accountsCreated: number; dealsCreated: number; positionsCreated: number; skipped?: boolean }>(
        (await api.post("/admin/trading/seed", { force: !!force })).data,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["trading-accounts"] });
      qc.invalidateQueries({ queryKey: ["trading-server-info"] });
      qc.invalidateQueries({ queryKey: ["trading-history"] });
      qc.invalidateQueries({ queryKey: ["trading-positions"] });
    },
  });
}
